// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Form, Input, Switch, Button, Checkbox, Pagination, Spin} from "antd";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {handleCheckBoxText} from "../../../utils/helpers";

import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer,
    MergeOptionsWrapper,
    FilterWrapper,
    MergeOptionsTableWrap,
    MergeOptionsList,
    MergeOptionsListRow,
    SubCompanyTableWrap,
    SubCompanyCardList,
    SubCompanyCard,
    SubCompanyCardTitle,
    SubCompanyCardRow,
    SubCompanyCardActions
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {queueOptionsValidation} from "./queueOptionsValidation";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const QueueOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText
                  })
                : () => [],
        dependencies: [companyData, currentPage, subCompanyRecall, city, searchText]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                queue: {
                    isActive: companyData?.data?.options?.queue?.isActive,
                    maxCount: companyData?.data?.options?.queue?.maxCount || 0,
                    maxWaitingTime: companyData?.data?.options?.queue?.maxWaitingTime || 0,
                    thresholdCount: companyData?.data?.options?.queue?.thresholdCount || 0,
                    thresholdTime: companyData?.data?.options?.queue?.thresholdTime || 0,
                    applyQueueOptions: false
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        validationSchema: queueOptionsValidation,
        onSubmit: async values => {
            setCompanyLoading(true);
            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            await stores.companyStore.updateCompanyQueueOptions(values);
            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.queue;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        if (submitData.maxCount <= 0 || submitData.maxCount.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.queueOptions.maxOrderCount.label", {
                    ns: "common"
                })} (${t("fields.queueOptions.maxOrderCount.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (submitData.maxWaitingTime <= 0 || submitData.maxWaitingTime.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.queueOptions.maxWaitingTime.label", {
                    ns: "common"
                })} (${t("fields.queueOptions.maxWaitingTime.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (submitData.thresholdCount <= 0 || submitData.thresholdCount.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.queueOptions.thresholdCount.label", {
                    ns: "common"
                })} (${t("fields.queueOptions.thresholdCount.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (submitData.thresholdTime <= 0 || submitData.thresholdTime.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.queueOptions.thresholdTime.label", {
                    ns: "common"
                })}(${t("fields.queueOptions.thresholdTime.short", {
                    ns: "common"
                })}) ${t("validation.greaterThanZero", {ns: "common"})}`,
                onOk: () => {}
            });
        } else {
            const params = {
                subCompanyId: submitData._id,
                options: {
                    isActive: submitData.isActive,
                    maxCount: submitData.maxCount,
                    maxWaitingTime: submitData.maxWaitingTime,
                    thresholdCount: submitData.thresholdCount,
                    thresholdTime: submitData.thresholdTime
                }
            };
            const result = await stores.companyStore.updateSubCompanyQueueOptions(params);
            if (result) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
        }
        setIsSubCompanySubmitting(false);
    };

    return (
        <MergeOptionsWrapper data-testid="queueOptionsScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    onFinish={handleSubmit}
                    data-testid="queueOptionsForm"
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <ErrorFocus formik={formik} />
                    <FormikProvider value={formik}>
                    <MergeOptionsTableWrap>
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        pagination={false}
                        data-testid="queueTable"
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                            <Column
                                title={`${t("fields.queueOptions.orderQueue.label", {
                                    ns: "common"
                                })} (${t("fields.queueOptions.orderQueue.short", {ns: "common"})})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.queue.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="orderQueueOptionSwitch"
                                            onChange={e => {
                                                setFieldValue("options.queue.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.queueOptions.maxOrderCount.label", {
                                    ns: "common"
                                })} (${t("fields.queueOptions.maxOrderCount.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="maxCount"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.queue.maxCount`) &&
                                            getIn(touched, `options.queue.maxCount`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.queue.maxCount`) &&
                                            getIn(touched, `options.queue.maxCount`)
                                                ? getIn(errors, `options.queue.maxCount`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="maxCount"
                                            data-testid="maxCountInput"
                                            value={
                                                values.options.queue.isActive
                                                    ? values.options.queue.maxCount
                                                    : companyData?.data?.options?.queue?.maxCount
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.queue.maxCount",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.queue.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.queueOptions.maxWaitingTime.label", {
                                    ns: "common"
                                })} (${t("fields.queueOptions.maxWaitingTime.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="maxWaitingTime"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.queue.maxWaitingTime`) &&
                                            getIn(touched, `options.queue.maxWaitingTime`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.queue.maxWaitingTime`) &&
                                            getIn(touched, `options.queue.maxWaitingTime`)
                                                ? getIn(errors, `options.queue.maxWaitingTime`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="maxWaitingTime"
                                            data-testid="maxWaitingTimeInput"
                                            value={
                                                values.options.queue.isActive
                                                    ? values.options.queue.maxWaitingTime
                                                    : companyData?.data?.options?.queue
                                                          ?.maxWaitingTime
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.queue.maxWaitingTime",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.queue.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.queueOptions.thresholdCount.label", {
                                    ns: "common"
                                })} (${t("fields.queueOptions.thresholdCount.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="thresholdCount"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.queue.thresholdCount`) &&
                                            getIn(touched, `options.queue.thresholdCount`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.queue.thresholdCount`) &&
                                            getIn(touched, `options.queue.thresholdCount`)
                                                ? getIn(errors, `options.queue.thresholdCount`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="thresholdCount"
                                            data-testid="thresholdCountInput"
                                            value={
                                                values.options.queue.isActive
                                                    ? values.options.queue.thresholdCount
                                                    : companyData?.data?.options?.queue
                                                          ?.thresholdCount
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.queue.thresholdCount",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.queue.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.queueOptions.thresholdTime.label", {
                                    ns: "common"
                                })} (${t("fields.queueOptions.thresholdTime.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="thresholdTime"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.queue.thresholdTime`) &&
                                            getIn(touched, `options.queue.thresholdTime`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.queue.thresholdTime`) &&
                                            getIn(touched, `options.queue.thresholdTime`)
                                                ? getIn(errors, `options.queue.thresholdTime`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="thresholdTime"
                                            data-testid="thresholdTimeInput"
                                            value={
                                                values.options.queue.isActive
                                                    ? values.options.queue.thresholdTime
                                                    : companyData?.data?.options?.queue
                                                          ?.thresholdTime
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.queue.thresholdTime",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.queue.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                    </Table>
                    </MergeOptionsTableWrap>

                    <MergeOptionsList>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("fields.queueOptions.orderQueue.label", {ns: "common"})} (
                                {t("fields.queueOptions.orderQueue.short", {ns: "common"})})
                            </span>
                            <Form.Item>
                                <Switch
                                    checked={values.options.queue.isActive}
                                    checkedChildren={t("other.active", {ns: "common"})}
                                    unCheckedChildren={t("other.passive", {ns: "common"})}
                                    onChange={e => {
                                        setFieldValue("options.queue.isActive", e);
                                        setIsCompanyFormChanged(true);
                                    }}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("fields.queueOptions.maxOrderCount.label", {ns: "common"})} (
                                {t("fields.queueOptions.maxOrderCount.short", {ns: "common"})})
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.queue.maxCount") &&
                                    getIn(touched, "options.queue.maxCount")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.queue.maxCount") &&
                                    getIn(touched, "options.queue.maxCount")
                                        ? getIn(errors, "options.queue.maxCount")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options.queue.isActive
                                            ? values.options.queue.maxCount
                                            : companyData?.data?.options?.queue?.maxCount
                                    }
                                    onChange={e =>
                                        setFieldValue("options.queue.maxCount", e.target.value || "")
                                    }
                                    disabled={!values.options.queue.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("fields.queueOptions.maxWaitingTime.label", {ns: "common"})} (
                                {t("fields.queueOptions.maxWaitingTime.short", {ns: "common"})})
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.queue.maxWaitingTime") &&
                                    getIn(touched, "options.queue.maxWaitingTime")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.queue.maxWaitingTime") &&
                                    getIn(touched, "options.queue.maxWaitingTime")
                                        ? getIn(errors, "options.queue.maxWaitingTime")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options.queue.isActive
                                            ? values.options.queue.maxWaitingTime
                                            : companyData?.data?.options?.queue?.maxWaitingTime
                                    }
                                    onChange={e =>
                                        setFieldValue(
                                            "options.queue.maxWaitingTime",
                                            e.target.value || ""
                                        )
                                    }
                                    disabled={!values.options.queue.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("fields.queueOptions.thresholdCount.label", {ns: "common"})} (
                                {t("fields.queueOptions.thresholdCount.short", {ns: "common"})})
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.queue.thresholdCount") &&
                                    getIn(touched, "options.queue.thresholdCount")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.queue.thresholdCount") &&
                                    getIn(touched, "options.queue.thresholdCount")
                                        ? getIn(errors, "options.queue.thresholdCount")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options.queue.isActive
                                            ? values.options.queue.thresholdCount
                                            : companyData?.data?.options?.queue?.thresholdCount
                                    }
                                    onChange={e =>
                                        setFieldValue(
                                            "options.queue.thresholdCount",
                                            e.target.value || ""
                                        )
                                    }
                                    disabled={!values.options.queue.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("fields.queueOptions.thresholdTime.label", {ns: "common"})} (
                                {t("fields.queueOptions.thresholdTime.short", {ns: "common"})})
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.queue.thresholdTime") &&
                                    getIn(touched, "options.queue.thresholdTime")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.queue.thresholdTime") &&
                                    getIn(touched, "options.queue.thresholdTime")
                                        ? getIn(errors, "options.queue.thresholdTime")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options.queue.isActive
                                            ? values.options.queue.thresholdTime
                                            : companyData?.data?.options?.queue?.thresholdTime
                                    }
                                    onChange={e =>
                                        setFieldValue(
                                            "options.queue.thresholdTime",
                                            e.target.value || ""
                                        )
                                    }
                                    disabled={!values.options.queue.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                    </MergeOptionsList>
                    </FormikProvider>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            data-testid="applyForAllSubCompaniesCheckbox"
                            checked={values?.applyQueueOptions}
                            onChange={e => setFieldValue("applyQueueOptions", e.target.checked)}
                        >
                            {handleCheckBoxText(searchText, city)}
                        </Checkbox>
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                            data-testid="submitButton"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <FilterWrapper>
                <Col span={12} data-testid="filterCard">
                    <FilterContainer type="flex" gutter={[20, 20]}>
                        <Col span={6}>
                            <CityFilter
                                value={city}
                                onChange={value => {
                                    setCurrentPage(1);
                                    setCity(value);
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <h5>{t("filters.search", {ns: "common"})}</h5>
                            <Input.Search
                                value={text}
                                data-testid="searchInput"
                                onSearch={value => {
                                    setCurrentPage(1);
                                    setSearchText(value);
                                }}
                                onChange={event => {
                                    setText(event.target.value);
                                }}
                            ></Input.Search>
                        </Col>
                    </FilterContainer>
                </Col>
            </FilterWrapper>
            <SubCompanyContainer>
                <SubCompanyTableWrap>
                <Table
                    rowKey="_id"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    data-testid="subCompaniesTable"
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push("/companies/queue-options/" + companyId + "?page=" + page);
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-testid="name"> {text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-testid="city"> {text}</span>}
                    />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.queueOptions.orderQueue.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="orderQueueOptionSwitch"
                                    checked={subCompanyData.rows[index]?.options?.queue?.isActive}
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.queue.isActive = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="maxCount"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.queueOptions.maxOrderCount.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    disabled={!subCompanyData.rows[index]?.options?.queue?.isActive}
                                    type="number"
                                    name="maxCount"
                                    data-testid="maxCountInput"
                                    value={subCompanyData.rows[index]?.options?.queue?.maxCount}
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.queue.maxCount = e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="maxWaitingTime"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.queueOptions.maxWaitingTime.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="maxWaitingTime"
                                    data-testid="maxWaitingTimeInput"
                                    disabled={!subCompanyData.rows[index]?.options?.queue?.isActive}
                                    value={
                                        subCompanyData.rows[index]?.options?.queue?.maxWaitingTime
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.queue.maxWaitingTime =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="thresholdCount"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.queueOptions.thresholdCount.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="thresholdCount"
                                    data-testid="thresholdCountInput"
                                    disabled={!subCompanyData.rows[index]?.options?.queue?.isActive}
                                    value={
                                        subCompanyData.rows[index]?.options?.queue?.thresholdCount
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.queue.thresholdCount =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="thresholdTime"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.queueOptions.thresholdTime.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="thresholdTime"
                                    data-testid="thresholdTimeInput"
                                    disabled={!subCompanyData.rows[index]?.options?.queue?.isActive}
                                    value={
                                        subCompanyData.rows[index]?.options?.queue?.thresholdTime
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.queue.thresholdTime =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
                </SubCompanyTableWrap>

                <SubCompanyCardList>
                    {subCompanyLoading ? (
                        <div style={{textAlign: "center", padding: 24}}>
                            <Spin size="large" />
                        </div>
                    ) : !subCompanyData?.rows?.length ? (
                        <EmptyMessage />
                    ) : (
                        <>
                            {(subCompanyData.rows || []).map((record, index) => {
                                const queueOpt = record?.options?.queue || {};
                                const setQueue = (field, value) => {
                                    setShowSubmitButton(prev => ({...prev, [record._id]: true}));
                                    setSubCompanyData(prevData => {
                                        const newData = {...prevData};
                                        const newItem = {...newData.rows[index]};
                                        if (!newItem.options) newItem.options = {};
                                        if (!newItem.options.queue) newItem.options.queue = {};
                                        newItem.options.queue[field] = value;
                                        newData.rows[index] = newItem;
                                        return newData;
                                    });
                                };
                                return (
                                    <SubCompanyCard key={record._id}>
                                        <SubCompanyCardTitle>
                                            {record.name} · {record.city}
                                        </SubCompanyCardTitle>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("fields.queueOptions.orderQueue.short", {ns: "common"})}
                                            </span>
                                            <Switch
                                                checked={queueOpt.isActive}
                                                checkedChildren={t("other.active", {ns: "common"})}
                                                unCheckedChildren={t("other.passive", {ns: "common"})}
                                                onChange={e => setQueue("isActive", e)}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("fields.queueOptions.maxOrderCount.short", {ns: "common"})}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!queueOpt.isActive}
                                                value={queueOpt.maxCount}
                                                onChange={e => setQueue("maxCount", e.target?.value || "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("fields.queueOptions.maxWaitingTime.short", {ns: "common"})}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!queueOpt.isActive}
                                                value={queueOpt.maxWaitingTime}
                                                onChange={e => setQueue("maxWaitingTime", e.target?.value || "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("fields.queueOptions.thresholdCount.short", {ns: "common"})}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!queueOpt.isActive}
                                                value={queueOpt.thresholdCount}
                                                onChange={e => setQueue("thresholdCount", e.target?.value || "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("fields.queueOptions.thresholdTime.short", {ns: "common"})}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!queueOpt.isActive}
                                                value={queueOpt.thresholdTime}
                                                onChange={e => setQueue("thresholdTime", e.target?.value || "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardActions>
                                            {showSubmitButton[record._id] && (
                                                <Button
                                                    disabled={isSubCompanySubmitting}
                                                    onClick={() => onSubCompanySubmit(index)}
                                                    type="primary"
                                                    block
                                                >
                                                    {t("actions.save", {ns: "common"})}
                                                </Button>
                                            )}
                                        </SubCompanyCardActions>
                                    </SubCompanyCard>
                                );
                            })}
                            {subCompanyData?.dataCount > 10 && (
                                <div style={{marginTop: 16, display: "flex", justifyContent: "center"}}>
                                    <Pagination
                                        total={subCompanyData.dataCount}
                                        current={currentPage}
                                        onChange={page => {
                                            setCurrentPage(page);
                                            history.push("/companies/queue-options/" + companyId + "?page=" + page);
                                        }}
                                        hideOnSinglePage
                                        showSizeChanger={false}
                                        pageSize={10}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </SubCompanyCardList>
            </SubCompanyContainer>
        </MergeOptionsWrapper>
    );
};
